import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  createRef
} from "react"
import ContentEditable from "react-contenteditable"
import { debounce } from "ts-debounce"
import backend from "~/backend"
import useDidUpdateEffect from "~/hooks/useDidUpdateEffect"
import { useDetectClickOutside } from "react-detect-click-outside"

export type Props = {
  dayId: number
  initialTasks: Task[]
  dailies: DailyProps[]
}

type Task = {
  index: string
  task: string
  completed: boolean
  repeatable_task_id?: number
  ref?: React.MutableRefObject<null | HTMLElement>
}

type DailyProps = {
  id: number
  task: string
  due: boolean
  due_by: number
}

type TasksMap = {
  [index: string]: Task
}

const ThreeTasks = ({ dayId, initialTasks, dailies }: Props) => {
  const tasksMap: TasksMap = Object.fromEntries(
    initialTasks.map((task) => [task.index, { ...task, ref: createRef() }])
  )

  const [tasks, setTasks] = useState(tasksMap)
  const [jumpIndex, setJumpIndex] = useState<null | {
    index: string
    direction: number
  }>(null)
  const [borderClass, setBorderClass] = useState("border-danger")

  const updateTask = (index: string, attributes: { task: string }) => {
    setTasks((previous) => {
      return {
        ...previous,
        [index]: { ...previous[index], ...attributes }
      }
    })
  }

  const toggleTask = (index: string) => {
    setTasks((previous) => {
      const task = previous[index]

      return {
        ...previous,
        [task.index]: { ...task, completed: !task.completed }
      }
    })
  }

  const focusPendingRef = useRef<string | null>(null)

  const addTask = (
    initialText: string = "",
    { focus = true }: { focus?: boolean } = {}
  ) => {
    setTasks((prev) => {
      const nextIdx = String(Math.max(...Object.keys(prev).map(Number)) + 1)
      if (focus) focusPendingRef.current = nextIdx
      return {
        ...prev,
        [nextIdx]: {
          index: nextIdx,
          task: initialText,
          completed: false,
          ref: createRef()
        }
      }
    })
  }

  const deleteTask = (index: string) => {
    if (parseInt(index) <= 3) return

    setTasks((prev) => {
      const remaining = Object.values(prev)
        .filter((t) => t.index !== index)
        .sort((a, b) => parseInt(a.index) - parseInt(b.index))
        .map((t, i) => ({ ...t, index: String(i + 1) }))

      return Object.fromEntries(remaining.map((t) => [t.index, t]))
    })
  }

  const persistChanges = (payload: Task[]) => {
    backend.updateThreeTasks(dayId, payload)
  }

  const debouncedPersist = useCallback(debounce(persistChanges, 300), [])

  const handleKeyUp = (
    index: string,
    event: React.KeyboardEvent<HTMLDivElement>
  ) => {
    if (event.ctrlKey && (event.key == "Enter" || event.key == "d")) {
      event.preventDefault()
      toggleTask(index)
    } else if (event.shiftKey && event.key == "Enter") {
      event.preventDefault()
      setJumpIndex({ index, direction: -1 })
    } else if (!event.shiftKey && event.key == "Enter") {
      event.preventDefault()
      setJumpIndex({ index, direction: 1 })
    } else if (event.ctrlKey && event.key == "j") {
      setJumpIndex({ index, direction: 1 })
    } else if (event.ctrlKey && event.key == "k") {
      setJumpIndex({ index, direction: -1 })
    }
  }

  const jump = ({ index, direction }: { index: string; direction: number }) => {
    const target = parseInt(index) + direction

    if (tasks[target] !== undefined) {
      const ref = tasks[target].ref!.current

      if (ref) {
        ref.focus()

        if (ref.firstChild) {
          let range = new Range()
          range.setStart(ref.firstChild, ref.firstChild.length)
          let selection = window.getSelection()!
          selection.removeAllRanges()
          selection.addRange(range)
        }
      }
    }
  }

  useDidUpdateEffect(() => {
    const borderClass = Object.values(tasks).every((task) => task.completed)
      ? "border-success"
      : "border-danger"
    setBorderClass(borderClass)

    const payload: Task[] = Object.values(tasks).map((el) => {
      return {
        index: el.index,
        task: el.task,
        completed: el.completed
      }
    })

    debouncedPersist(payload)
  }, [tasks])

  useEffect(() => {
    const borderClass = Object.values(tasks).every((task) => task.completed)
      ? "border-success"
      : "border-danger"
    setBorderClass(borderClass)
  }, [tasks])

  useDidUpdateEffect(() => {
    if (jumpIndex == null) return

    jump(jumpIndex)

    setJumpIndex(null)
  }, [jumpIndex])

  useDidUpdateEffect(() => {
    const idx = focusPendingRef.current
    if (!idx) return

    const el = tasks[idx]?.ref?.current
    if (el) {
      el.focus()
      focusPendingRef.current = null
    }
  }, [tasks])

  const insertDaily = (daily: DailyProps) => {
    if (Object.values(tasks).some((t) => t.task === daily.task)) return

    const slot = Object.values(tasks).find((task) => {
      if (task && task.ref && task.ref.current) {
        return task.ref.current.textContent.length == 0 && !task.completed
      }
    })

    if (slot) {
      updateTask(slot.index, { task: daily.task })
    } else {
      addTask(daily.task, { focus: false })
    }
  }

  return (
    <div className="mt-4 grow">
      <div className={`group space-y-1  border-l-4 px-4 ${borderClass}`}>
        <div className="flex items-center">
          <div className="mb-2 grow text-lg font-medium">Focus</div>
          <button
            type="button"
            onClick={() => addTask()}
            title="Add task"
            className="mr-2 text-slate opacity-0 transition hover:font-medium hover:text-primary focus:opacity-100 group-hover:opacity-100"
          >
            +
          </button>
          <Dailies dailies={dailies} insertDaily={insertDaily} />
        </div>

        {Object.entries(tasks).map(([index, task]) => (
          <React.Fragment key={index}>
            {parseInt(index) === 4 && (
              <div className="flex items-center gap-3 py-2">
                <span className="font-mono text-4xs uppercase tracking-widest text-fg-muted">
                  Also today
                </span>
                <span className="h-px grow bg-border" />
              </div>
            )}
            <div className="group/row flex items-center space-x-2">
              <div>
                <input
                  type="checkbox"
                  id={`three-tasks-${task.index}`}
                  className="checkbox-input cursor-pointer"
                  onChange={() => {
                    toggleTask(index)
                  }}
                  checked={task.completed}
                />
                <label htmlFor={`three-tasks-${index}`}>
                  <span className="checkbox cursor-pointer"></span>
                </label>
              </div>
              <ContentEditable
                innerRef={task.ref as React.RefObject<HTMLElement>}
                html={task.task || ""}
                onKeyDown={(event) => handleKeyUp(index, event)}
                onChange={(event) => {
                  updateTask(index, { task: event.target.value })
                }}
                className={`w-full focus:outline-none ${
                  task.completed ? "checkbox-checked" : ""
                }`}
              />
              {parseInt(index) > 3 && (
                <button
                  type="button"
                  onClick={() => deleteTask(index)}
                  title="Remove task"
                  className="group-hover/row:opacity-100 text-xs text-danger opacity-0 transition hover:text-danger-hover"
                >
                  <i className="fa-regular fa-trash-can text-xs" />
                </button>
              )}
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  )
}

type DailiesProps = {
  dailies: DailyProps[]
  insertDaily: (daily: DailyProps) => void
}

const Dailies = ({ dailies, insertDaily }: DailiesProps) => {
  const [opened, setOpened] = useState(false)
  const [filter, setFilter] = useState("")
  const inputRef = useRef<HTMLInputElement | null>(null)
  const ref = useDetectClickOutside({
    onTriggered: () => {
      setOpened(false)
    }
  })

  useEffect(() => {
    if (opened) {
      setFilter("")
      inputRef.current?.focus()
    }
  }, [opened])

  const needle = filter.trim().toLowerCase()
  const filteredDailies =
    needle === ""
      ? dailies
      : dailies.filter((d) => d.task.toLowerCase().includes(needle))

  return (
    <div className="relative" ref={ref}>
      <a
        className="btn btn-outline btn-xs rounded-lg"
        onClick={() => setOpened((previous) => !previous)}
      >
        Dailies
      </a>

      <div
        className={`${
          opened ? "flex" : "hidden"
        } inner absolute right-0 z-10 mt-1 w-72 flex-col rounded bg-white shadow-lg`}
      >
        <div className="border-b border-border p-2">
          <input
            ref={inputRef}
            type="text"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            placeholder="Filter dailies..."
            className="form-field form-field-xs"
          />
        </div>

        <div className="max-h-80 overflow-y-auto">
          {filteredDailies.length === 0 && (
            <div className="px-3 py-2 text-sm text-fg-muted">No matches</div>
          )}

          {filteredDailies.map((daily) => (
            <div key={daily.id} className="flex items-center">
              <a
                className="flex grow cursor-pointer items-center space-x-2  rounded px-3 py-2 hover:bg-surface"
                onClick={() => insertDaily(daily)}
              >
                <div className="grow text-sm text-sapphire">{daily.task}</div>

                {daily.due_by >= 0 && (
                  <div className="whitespace-nowrap text-tiny font-semibold text-danger">
                    {daily.due_by == 666
                      ? "Never done"
                      : daily.due_by == 0
                      ? "Todo"
                      : `${daily.due_by} days late`}
                  </div>
                )}
              </a>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default ThreeTasks
